import { $t, updatePreset, updateSurfacePalette } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import Lara from '@primeng/themes/lara';
import Nora from '@primeng/themes/nora';
import { SpaPreset } from '../../spa-preset';

export const PRESETS = {
  Aura,
  Lara,
  Nora,
  Spa: SpaPreset
} as const;

export type KeyOfType<T> = keyof T extends infer U ? U : never;

export type SurfacesType = {
  name?: string;
  palette?: {
    0?: string;
    50?: string;
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    500?: string;
    600?: string;
    700?: string;
    800?: string;
    900?: string;
    950?: string;
  };
};

/** Paleta pública que decide el dueño en /admin/diseno/apariencia y guarda el servidor. */
export interface TemaSitio {
  primary_color: string;
  surface: string;
  preset: string;
  dark_mode: boolean;
}

export const TEMA_POR_DEFECTO: TemaSitio = {
  primary_color: 'rose',
  surface: 'viva',
  preset: 'Spa',
  dark_mode: false
};

export const SURFACES: SurfacesType[] = [
  {
    name: 'slate',
    palette: {
      0: '#ffffff',
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
      950: '#020617'
    }
  },
  {
    name: 'gray',
    palette: {
      0: '#ffffff',
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
      950: '#030712'
    }
  },
  {
    name: 'zinc',
    palette: {
      0: '#ffffff',
      50: '#fafafa',
      100: '#f4f4f5',
      200: '#e4e4e7',
      300: '#d4d4d8',
      400: '#a1a1aa',
      500: '#71717a',
      600: '#52525b',
      700: '#3f3f46',
      800: '#27272a',
      900: '#18181b',
      950: '#09090b'
    }
  },
  {
    name: 'neutral',
    palette: {
      0: '#ffffff',
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
      950: '#0a0a0a'
    }
  },
  {
    name: 'stone',
    palette: {
      0: '#ffffff',
      50: '#fafaf9',
      100: '#f5f5f4',
      200: '#e7e5e4',
      300: '#d6d3d1',
      400: '#a8a29e',
      500: '#78716c',
      600: '#57534e',
      700: '#44403c',
      800: '#292524',
      900: '#1c1917',
      950: '#0c0a09'
    }
  },
  {
    name: 'soho',
    palette: {
      0: '#ffffff',
      50: '#ececec',
      100: '#dedfdf',
      200: '#c4c4c6',
      300: '#adaeb0',
      400: '#97979b',
      500: '#7f8084',
      600: '#6a6b70',
      700: '#55565b',
      800: '#3f4046',
      900: '#2c2c34',
      950: '#16161d'
    }
  },
  {
    name: 'viva',
    palette: {
      0: '#ffffff',
      50: '#faf8f6',
      100: '#f3efec',
      200: '#e6e0dc',
      300: '#d4ccc6',
      400: '#a39a94',
      500: '#7d746e',
      600: '#655d58',
      700: '#4f4844',
      800: '#373230',
      900: '#25201e',
      950: '#131010'
    }
  },
  {
    name: 'ocean',
    palette: {
      0: '#ffffff',
      50: '#fbfcfc',
      100: '#F7F9F8',
      200: '#EFF3F2',
      300: '#DADEDD',
      400: '#B1B7B6',
      500: '#828787',
      600: '#5F7274',
      700: '#415B61',
      800: '#29444E',
      900: '#183240',
      950: '#0c1920'
    }
  }
];

const COLORES_BASE = [
  'emerald', 'green', 'lime', 'orange', 'amber', 'yellow', 'teal', 'cyan',
  'sky', 'blue', 'indigo', 'violet', 'purple', 'fuchsia', 'pink', 'rose'
];

export function primaryColorsFor(preset: string | undefined): SurfacesType[] {
  const presetPalette = (PRESETS[(preset ?? 'Spa') as KeyOfType<typeof PRESETS>] ?? PRESETS.Spa).primitive;
  const palettes: SurfacesType[] = [{ name: 'noir', palette: {} }];

  COLORES_BASE.forEach((color) => {
    palettes.push({
      name: color,
      palette: presetPalette?.[color as KeyOfType<typeof presetPalette>] as SurfacesType['palette']
    });
  });

    palettes.push({
      name: 'CNB BLUE',
      palette: {
        0: '#ffffff',
        50: '#f3f5fa',
        100: '#e7ebf5',
        200: '#cfd7eb',
        300: '#a7b9db',
        400: '#8a9dc9',
        500: '#7283b0',
        600: '#5a6990',
        700: '#454f70',
        800: '#303750',
        900: '#1c2130',
        950: '#0e1118'
      }
    });

    palettes.push({
      name: 'CNB GREEN',
      palette: {
        0: '#ffffff',
        50: '#eaf4f2',
        100: '#d5e9e5',
        200: '#accdc9',
        300: '#83b1ad',
        400: '#6b9d95',
        500: '#648b81',
        600: '#4f6f66',
        700: '#3a534c',
        800: '#253732',
        900: '#101b18',
        950: '#080e0c'
      }
    });
  return palettes;
}

export function presetExt(primary: string | undefined, preset: string | undefined) {
  const color: SurfacesType = primaryColorsFor(preset).find((c) => c.name === primary) || {};

    if (color.name === 'noir') {
      return {
        semantic: {
          primary: {
            50: '{surface.50}',
            100: '{surface.100}',
            200: '{surface.200}',
            300: '{surface.300}',
            400: '{surface.400}',
            500: '{surface.500}',
            600: '{surface.600}',
            700: '{surface.700}',
            800: '{surface.800}',
            900: '{surface.900}',
            950: '{surface.950}'
          },
          colorScheme: {
            light: {
              primary: {
                color: '{primary.950}',
                contrastColor: '#ffffff',
                hoverColor: '{primary.800}',
                activeColor: '{primary.700}'
              },
              highlight: {
                background: '{primary.950}',
                focusBackground: '{primary.700}',
                color: '#ffffff',
                focusColor: '#ffffff'
              }
            },
            dark: {
              primary: {
                color: '{primary.50}',
                contrastColor: '{primary.950}',
                hoverColor: '{primary.200}',
                activeColor: '{primary.300}'
              },
              highlight: {
                background: '{primary.50}',
                focusBackground: '{primary.300}',
                color: '{primary.950}',
                focusColor: '{primary.950}'
              }
            }
          }
        }
      };
    } else {
      if (preset === 'Nora') {
        return {
          semantic: {
            primary: color.palette,
            colorScheme: {
              light: {
                primary: {
                  color: '{primary.600}',
                  contrastColor: '#ffffff',
                  hoverColor: '{primary.700}',
                  activeColor: '{primary.800}'
                },
                highlight: {
                  background: '{primary.600}',
                  focusBackground: '{primary.700}',
                  color: '#ffffff',
                  focusColor: '#ffffff'
                }
              },
              dark: {
                primary: {
                  color: '{primary.500}',
                  contrastColor: '{surface.900}',
                  hoverColor: '{primary.400}',
                  activeColor: '{primary.300}'
                },
                highlight: {
                  background: '{primary.500}',
                  focusBackground: '{primary.400}',
                  color: '{surface.900}',
                  focusColor: '{surface.900}'
                }
              }
            }
          }
        };
      } else {
        return {
          semantic: {
            primary: color.palette,
            colorScheme: {
              light: {
                primary: {
                  color: '{primary.500}',
                  contrastColor: '#ffffff',
                  hoverColor: '{primary.600}',
                  activeColor: '{primary.700}'
                },
                highlight: {
                  background: '{primary.50}',
                  focusBackground: '{primary.100}',
                  color: '{primary.700}',
                  focusColor: '{primary.800}'
                }
              },
              dark: {
                primary: {
                  color: '{primary.400}',
                  contrastColor: '{surface.900}',
                  hoverColor: '{primary.300}',
                  activeColor: '{primary.200}'
                },
                highlight: {
                  background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                  focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                  color: 'rgba(255,255,255,.87)',
                  focusColor: 'rgba(255,255,255,.87)'
                }
              }
            }
          }
        };
      }
    }
  }

/** Normaliza un tema recibido del servidor: cualquier valor desconocido vuelve al de por defecto. */
export function normalizarTema(raw: Partial<TemaSitio> | null | undefined): TemaSitio {
  const preset = raw?.preset && raw.preset in PRESETS ? raw.preset : TEMA_POR_DEFECTO.preset;
  const primary = primaryColorsFor(preset).some((c) => c.name === raw?.primary_color)
    ? (raw!.primary_color as string)
    : TEMA_POR_DEFECTO.primary_color;
  const surface = SURFACES.some((s) => s.name === raw?.surface) ? (raw!.surface as string) : TEMA_POR_DEFECTO.surface;
  return { primary_color: primary, surface, preset, dark_mode: raw?.dark_mode === true };
}

/** Aplica preset, color primario y tono de fondo al tema de PrimeNG. */
export function aplicarTemaSitio(tema: TemaSitio): void {
  const preset = PRESETS[tema.preset as KeyOfType<typeof PRESETS>] ?? PRESETS.Spa;
  const surfacePalette = SURFACES.find((s) => s.name === tema.surface)?.palette;
  $t()
    .preset(preset)
    .preset(presetExt(tema.primary_color, tema.preset))
    .surfacePalette(surfacePalette)
    .use({ useDefaultOptions: true });
}

export function aplicarColorPrimario(tema: TemaSitio): void {
  updatePreset(presetExt(tema.primary_color, tema.preset));
}

export function aplicarTonoFondo(tema: TemaSitio): void {
  const palette = SURFACES.find((s) => s.name === tema.surface)?.palette;
  if (palette) updateSurfacePalette(palette);
}
